import { useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Hook for sharing property analyses
 * Generates shareable links with expiry
 */
export function useShareAnalysis() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [shareUrl, setShareUrl] = useState(null);

    /**
     * Create a shareable link
     */
    const createShareLink = useCallback(async (propertyData, analysis, expiresInDays = 7) => {
        setLoading(true);
        setError(null);
        setShareUrl(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await axios.post(
                `${API_URL}/api/share`,
                {
                    propertyData,
                    analysis,
                    expiresInDays
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setShareUrl(response.data.shareUrl);
                return {
                    shareId: response.data.shareId,
                    shareUrl: response.data.shareUrl,
                    expiresAt: response.data.expiresAt
                };
            } else {
                throw new Error(response.data.error || 'Failed to create share link');
            }
        } catch (err) {
            console.error('Error creating share link:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Copy share URL to clipboard
     */
    const copyToClipboard = useCallback(async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            return true;
        } catch (err) {
            console.error('Failed to copy:', err);
            return false;
        }
    }, []);

    /**
     * Reset share state
     */
    const reset = useCallback(() => {
        setShareUrl(null);
        setError(null);
    }, []);

    return {
        loading,
        error,
        shareUrl,
        createShareLink,
        copyToClipboard,
        reset
    };
}
